import { useCallback, useEffect, useState } from 'preact/hooks';

interface Options<T> {
  readonly initial?: () => T | undefined;
}

export type AsyncResult<T> =
  | { readonly state: 'loading'; readonly value: T | undefined; readonly error: undefined }
  | { readonly state: 'success'; readonly value: T; readonly error: undefined }
  | { readonly state: 'error'; readonly value: T | undefined; readonly error: unknown };

export function useAsync<T>(
  load: (signal: AbortSignal) => Promise<T>,
  deps: unknown[] = [],
  { initial }: Options<T> = {},
): AsyncResult<T> {
  const [result, setResult] = useState<AsyncResult<T>>({
    state: 'loading',
    value: initial?.(),
    error: undefined,
  });

  const memoizedLoad = useCallback((signal: AbortSignal) => {
    setResult((current) => ({ ...current, state: 'loading', error: undefined }));

    load(signal)
      .then((value) => {
        if (signal.aborted) return;
        setResult({ state: 'success', value, error: undefined });
      })
      .catch((error) => {
        if (signal.aborted) return;
        setResult((current) => ({ ...current, state: 'error', error }));
      });
  }, deps);

  useEffect(() => {
    const ac = new AbortController();
    memoizedLoad(ac.signal);
    return () => ac.abort();
  }, [memoizedLoad]);

  return result;
}
